/* eslint-disable no-case-declarations */
 
 
 
 
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import type { EconomicCycle, MarketEvent, RegionalMarket } from '@/types/tycoon';

interface EconomicIndicator {
  name: string;
  value: number;
  trend: 'rising' | 'falling' | 'stable';
  volatility: number;
  impact: number; // Impact on market
}

interface CycleTrigger {
  type: 'time_based' | 'indicator_based' | 'external_shock';
  condition: string;
  threshold: number;
  probability: number;
}

interface EconomicShock {
  id: string;
  name: string;
  type: 'supply' | 'demand' | 'financial' | 'geopolitical';
  severity: 'minor' | 'moderate' | 'major' | 'catastrophic';
  duration: number; // months
  effects: {
    gdpGrowth: number;
    inflation: number;
    unemployment: number;
    consumerConfidence: number;
    marketVolatility: number;
  };
  affectedRegions: string[];
  recoveryPattern: 'v_shaped' | 'u_shaped' | 'l_shaped' | 'w_shaped';
}

export class EconomicCycleService {
  private static instance: EconomicCycleService;
  private economicCycle: EconomicCycle;
  private indicators: Map<string, EconomicIndicator> = new Map();
  private cycleTriggers: CycleTrigger[] = [];
  private activeShocks: EconomicShock[] = [];
  private cycleHistory: Array<{ timestamp: number; phase: EconomicCycle['phase']; duration: number }> = [];

  private constructor() {
    this.economicCycle = this.initializeEconomicCycle();
    this.initializeIndicators();
    this.initializeCycleTriggers();
  }

  public static getInstance(): EconomicCycleService {
    if (!EconomicCycleService.instance) {
      EconomicCycleService.instance = new EconomicCycleService();
    }
    return EconomicCycleService.instance;
  }

  // Phase 3: Advanced Economic Cycle Modeling
  public updateEconomicCycle(timeElapsed: number, marketData: {
    globalEvents: MarketEvent[];
    regionalMarkets: Map<string, RegionalMarket>;
    competitorActions: unknown[];
  }): EconomicCycle {
    // Update economic indicators
    this.updateIndicators(timeElapsed, marketData);

    // Check for cycle transitions
    this.checkCycleTransitions();

    // Process economic shocks
    this.processEconomicShocks(timeElapsed);

    // Update cycle parameters
    this.updateCycleParameters(timeElapsed);

    // Generate new shocks if conditions are met
    this.evaluateShockProbability(marketData);

    return this.economicCycle;
  }

  public simulateEconomicScenarios(timeHorizon: number): Array<{
    scenario: string;
    probability: number;
    timeline: Array<{ month: number; phase: EconomicCycle['phase']; impact: number }>;
    marketImpact: number;
  }> {
    const scenarios = [
      {
        scenario: 'Prolonged Growth',
        probability: this.calculateScenarioProbability('growth'),
        timeline: this.simulateGrowthScenario(timeHorizon),
        marketImpact: 1.2
      },
      {
        scenario: 'Recession Cycle',
        probability: this.calculateScenarioProbability('recession'),
        timeline: this.simulateRecessionScenario(timeHorizon),
        marketImpact: 0.6
      },
      {
        scenario: 'Economic Shock',
        probability: this.calculateScenarioProbability('shock'),
        timeline: this.simulateShockScenario(timeHorizon),
        marketImpact: 0.4
      },
      {
        scenario: 'Stagflation',
        probability: this.calculateScenarioProbability('stagflation'),
        timeline: this.simulateStagflationScenario(timeHorizon),
        marketImpact: 0.7
      },
      {
        scenario: 'Innovation Boom',
        probability: this.calculateScenarioProbability('innovation'),
        timeline: this.simulateInnovationBoom(timeHorizon),
        marketImpact: 1.5
      }
    ];

    return scenarios.sort((a, b) => b.probability - a.probability);
  }

  public calculateRegionalEconomicImpact(regionId: string): {
    cyclicImpact: number;
    shockImpact: number;
    recoverySpeed: number;
    vulnerability: number;
  } {
    const baseImpact = this.economicCycle.marketMultiplier;
    
    // Regional vulnerability factors
    const vulnerability = this.calculateRegionalVulnerability(regionId);
    
    // Shock impact calculation
    let shockImpact = 1.0;
    this.activeShocks.forEach(shock => {
      if (shock.affectedRegions.includes(regionId) || shock.affectedRegions.includes('global')) {
        const severityMultiplier = this.getShockSeverityMultiplier(shock.severity);
        shockImpact *= (1 + shock.effects.gdpGrowth * severityMultiplier);
      }
    });

    // Recovery speed based on economic structure
    const recoverySpeed = this.calculateRecoverySpeed(regionId);

    return {
      cyclicImpact: baseImpact,
      shockImpact: Math.max(0.3, shockImpact),
      recoverySpeed,
      vulnerability
    };
  }

  public generateEconomicEvent(): MarketEvent | null {
    const eventProbability = this.calculateEventProbability();
    
    if (Math.random() > eventProbability) return null;

    const eventTypes = [
      {
        type: 'interest_rate_change',
        probability: 0.3,
        impact: this.generateInterestRateEvent()
      },
      {
        type: 'trade_policy_change',
        probability: 0.2,
        impact: this.generateTradePolicyEvent()
      },
      {
        type: 'monetary_policy_shift',
        probability: 0.25,
        impact: this.generateMonetaryPolicyEvent()
      },
      {
        type: 'fiscal_stimulus',
        probability: 0.15,
        impact: this.generateFiscalStimulusEvent()
      },
      {
        type: 'currency_crisis',
        probability: 0.1,
        impact: this.generateCurrencyCrisisEvent()
      }
    ];

    const selectedEvent = this.selectWeightedEvent(eventTypes);
    return selectedEvent ? this.createMarketEvent(selectedEvent) : null;
  }

  public predictCycleTransition(): {
    nextPhase: EconomicCycle['phase'];
    probabilityByMonth: Array<{ month: number; probability: number }>;
    triggerFactors: string[];
    estimatedDuration: number;
  } {
    const currentPhase = this.economicCycle.phase;
    const nextPhase = this.getNextPhase(currentPhase);
    
    const triggerFactors = this.identifyTransitionTriggers(currentPhase, nextPhase);
    const probabilityByMonth = this.calculateTransitionProbabilities(12); // Next 12 months
    const estimatedDuration = this.estimatePhaseRemaining();

    return {
      nextPhase,
      probabilityByMonth,
      triggerFactors,
      estimatedDuration
    };
  }

  // Private implementation methods
  private updateIndicators(timeElapsed: number, marketData: Record<string, unknown>): void {
    this.indicators.forEach((indicator, name) => {
      // Update based on cycle phase
      const phaseEffect = this.getPhaseEffect(this.economicCycle.phase, name);
      
      // Update based on external factors
      const externalEffect = this.calculateExternalEffect(name, marketData);
      
      // Add volatility and trend
      const trendEffect = this.getTrendEffect(indicator);
      const volatilityEffect = (Math.random() - 0.5) * indicator.volatility;
      
      const newValue = indicator.value * (1 + phaseEffect + externalEffect + trendEffect + volatilityEffect);
      
      // Update trend based on recent changes
      const valueChange = newValue - indicator.value;
      indicator.trend = valueChange > 0.02 ? 'rising' : valueChange < -0.02 ? 'falling' : 'stable';
      indicator.value = Math.max(0, newValue);
    });
  }

  private checkCycleTransitions(): void {
    const currentPhase = this.economicCycle.phase;
    
    // Check time-based transitions
    if (this.economicCycle.remainingMonths <= 0) {
      this.transitionToNextPhase();
      return;
    }

    // Check indicator-based transitions
    this.cycleTriggers.forEach(trigger => {
      if (trigger.type === 'indicator_based' && this.evaluateTrigger(trigger)) {
        if (Math.random() < trigger.probability) {
          this.transitionToNextPhase();
        }
      }
    });

    // Check for external shock transitions
    if (this.activeShocks.length > 0) {
      const shockSeverity = this.calculateCombinedShockSeverity();
      if (shockSeverity > 0.7 && currentPhase !== 'recession') {
        this.forceTransition('recession');
      }
    }

    // Decrement remaining time
    this.economicCycle.remainingMonths = Math.max(0, this.economicCycle.remainingMonths - 1);
  }

  private processEconomicShocks(timeElapsed: number): void {
    this.activeShocks = this.activeShocks.filter(shock => {
      shock.duration -= 1; // Decrement monthly
      
      if (shock.duration <= 0) {
        // Shock ending - apply recovery
        this.applyShockRecovery(shock);
        return false; // Remove from active shocks
      }
      
      // Apply ongoing shock effects
      this.applyShockEffects(shock);
      return true; // Keep shock active
    });
  }

  private updateCycleParameters(timeElapsed: number): void {
    const phase = this.economicCycle.phase;
    
    // Update GDP growth based on phase and indicators
    const gdpIndicator = this.indicators.get('gdp_growth');
    if (gdpIndicator) {
      this.economicCycle.gdpGrowth = gdpIndicator.value;
    }

    // Update consumer confidence
    const confidenceIndicator = this.indicators.get('consumer_confidence');
    if (confidenceIndicator) {
      this.economicCycle.consumerConfidence = confidenceIndicator.value;
    }

    // Update unemployment
    const unemploymentIndicator = this.indicators.get('unemployment');
    if (unemploymentIndicator) {
      this.economicCycle.unemploymentRate = unemploymentIndicator.value;
    }

    // Update inflation
    const inflationIndicator = this.indicators.get('inflation');
    if (inflationIndicator) {
      this.economicCycle.inflationRate = inflationIndicator.value;
    }

    // Calculate market multiplier
    this.economicCycle.marketMultiplier = this.calculateMarketMultiplier();
  }

  private evaluateShockProbability(marketData: Record<string, unknown>): void {
    const baseShockProbability = 0.02; // 2% per month
    
    let adjustedProbability = baseShockProbability;

    // Increase probability during vulnerable phases
    if (this.economicCycle.phase === 'peak' || this.economicCycle.phase === 'expansion') {
      adjustedProbability *= 1.5;
    }

    // Market stress indicators
    const volatilityIndicator = this.indicators.get('market_volatility');
    if (volatilityIndicator && volatilityIndicator.value > 0.3) {
      adjustedProbability *= 1.3;
    }

    // Global events impact
    const globalEvents = marketData.globalEvents || [];
    const economicEvents = globalEvents.filter((event: MarketEvent) => event.type === 'economic');
    adjustedProbability *= (1 + economicEvents.length * 0.1);

    if (Math.random() < adjustedProbability) {
      const shock = this.generateRandomShock();
      this.activeShocks.push(shock);
    }
  }

  private calculateScenarioProbability(scenario: string): number {
    const currentPhase = this.economicCycle.phase;
    const cycleAge = this.economicCycle.duration - this.economicCycle.remainingMonths;
    
    const baseProbabilities: { [key: string]: number } = {
      growth: 0.3,
      recession: 0.15,
      shock: 0.1,
      stagflation: 0.05,
      innovation: 0.2
    };

    let probability = baseProbabilities[scenario] || 0.1;

    // Adjust based on current phase
    switch (scenario) {
      case 'growth':
        if (currentPhase === 'expansion' || currentPhase === 'recovery') probability *= 2;
        if (currentPhase === 'recession') probability *= 0.3;
        break;
      case 'recession':
        if (currentPhase === 'peak' || currentPhase === 'contraction') probability *= 2.5;
        if (currentPhase === 'recovery') probability *= 0.2;
        break;
      case 'shock':
        if (cycleAge > 60) probability *= 1.5; // Longer cycles more shock-prone
        break;
      case 'stagflation':
        const inflationIndicator = this.indicators.get('inflation');
        const gdpIndicator = this.indicators.get('gdp_growth');
        if (inflationIndicator && gdpIndicator) {
          if (inflationIndicator.value > 0.04 && gdpIndicator.value < 0.02) {
            probability *= 3;
          }
        }
        break;
      case 'innovation':
        const technologyEvents = this.cycleHistory.filter(cycle => 
          cycle.phase === 'expansion' && Date.now() - cycle.timestamp < 365 * 24 * 60 * 60 * 1000
        ).length;
        probability *= (1 + technologyEvents * 0.2);
        break;
    }

    return Math.min(probability, 0.8);
  }

  private simulateGrowthScenario(timeHorizon: number): Array<{ month: number; phase: EconomicCycle['phase']; impact: number }> {
    const timeline = [];
    let currentPhase: EconomicCycle['phase'] = 'expansion';
    
    for (let month = 1; month <= timeHorizon; month++) {
      let impact = 1.1 + (month / timeHorizon) * 0.2; // Accelerating growth
      
      if (month > timeHorizon * 0.8) {
        currentPhase = 'peak';
        impact = 1.3;
      }
      
      timeline.push({ month, phase: currentPhase, impact });
    }
    
    return timeline;
  }

  private simulateRecessionScenario(timeHorizon: number): Array<{ month: number; phase: EconomicCycle['phase']; impact: number }> {
    const timeline = [];
    
    for (let month = 1; month <= timeHorizon; month++) {
      let phase: EconomicCycle['phase'] = 'contraction';
      let impact = 0.8 - (month / timeHorizon) * 0.2; // Deepening contraction
      
      if (month < timeHorizon * 0.3) {
        phase = 'contraction';
      } else if (month < timeHorizon * 0.7) {
        phase = 'recession';
        impact = 0.5;
      } else {
        phase = 'recovery';
        impact = 0.7 + ((month - timeHorizon * 0.7) / (timeHorizon * 0.3)) * 0.3;
      }
      
      timeline.push({ month, phase, impact });
    }
    
    return timeline;
  }

  private simulateShockScenario(timeHorizon: number): Array<{ month: number; phase: EconomicCycle['phase']; impact: number }> {
    const timeline = [];
    const shockMonth = Math.floor(timeHorizon * 0.2); // Shock hits early
    
    for (let month = 1; month <= timeHorizon; month++) {
      let phase: EconomicCycle['phase'] = 'expansion';
      let impact = 1.0;
      
      if (month <= shockMonth) {
        impact = 1.05; // Pre-shock normal growth
      } else if (month <= shockMonth + 3) {
        phase = 'recession';
        impact = 0.3; // Severe immediate impact
      } else if (month <= shockMonth + 8) {
        phase = 'recovery';
        impact = 0.5 + ((month - shockMonth - 3) / 5) * 0.3; // Gradual recovery
      } else {
        phase = 'expansion';
        impact = 0.9 + ((month - shockMonth - 8) / (timeHorizon - shockMonth - 8)) * 0.2;
      }
      
      timeline.push({ month, phase, impact });
    }
    
    return timeline;
  }

  private simulateStagflationScenario(timeHorizon: number): Array<{ month: number; phase: EconomicCycle['phase']; impact: number }> {
    const timeline = [];
    
    for (let month = 1; month <= timeHorizon; month++) {
      const phase: EconomicCycle['phase'] = 'contraction'; // Persistent stagnation
      const impact = 0.7 + Math.sin(month / 6) * 0.1; // Oscillating around 0.7
      
      timeline.push({ month, phase, impact });
    }
    
    return timeline;
  }

  private simulateInnovationBoom(timeHorizon: number): Array<{ month: number; phase: EconomicCycle['phase']; impact: number }> {
    const timeline = [];
    
    for (let month = 1; month <= timeHorizon; month++) {
      let phase: EconomicCycle['phase'] = 'expansion';
      let impact = 1.0 + (month / timeHorizon) * 0.5; // Accelerating growth from innovation
      
      if (month > timeHorizon * 0.6) {
        phase = 'peak';
        impact = 1.5 + Math.sin(month / 3) * 0.1; // High, volatile growth
      }
      
      timeline.push({ month, phase, impact });
    }
    
    return timeline;
  }

  // Helper methods continue...
  private calculateRegionalVulnerability(regionId: string): number {
    // Simplified vulnerability calculation
    const vulnerabilityMap: { [key: string]: number } = {
      'emerging_markets': 0.8,
      'developed_markets': 0.4,
      'china': 0.6,
      'europe': 0.5,
      'north_america': 0.3
    };
    
    return vulnerabilityMap[regionId] || 0.5;
  }

  private calculateRecoverySpeed(regionId: string): number {
    // Economic resilience factors
    const resilienceMap: { [key: string]: number } = {
      'north_america': 0.8,
      'europe': 0.7,
      'china': 0.9,
      'emerging_markets': 0.4
    };
    
    return resilienceMap[regionId] || 0.6;
  }

  private getShockSeverityMultiplier(severity: EconomicShock['severity']): number {
    const multipliers = {
      minor: 0.3,
      moderate: 0.6,
      major: 1.0,
      catastrophic: 1.5
    };
    return multipliers[severity];
  }

  private calculateEventProbability(): number {
    const baseProb = 0.05; // 5% base monthly probability
    
    // Increase probability during unstable phases
    const phaseMultipliers = {
      recession: 1.5,
      recovery: 1.2,
      expansion: 1.0,
      peak: 1.3,
      contraction: 1.4
    };
    
    return baseProb * phaseMultipliers[this.economicCycle.phase];
  }

  // Event generators
  private generateInterestRateEvent(): any {
    const direction = Math.random() > 0.5 ? 'increase' : 'decrease';
    const magnitude = 0.25 + Math.random() * 0.5; // 0.25% to 0.75%
    
    return {
      direction,
      magnitude,
      marketImpact: direction === 'increase' ? -magnitude * 0.3 : magnitude * 0.2
    };
  }

  private generateTradePolicyEvent(): any {
    const policies = ['tariff_increase', 'trade_agreement', 'sanctions', 'subsidy_program'];
    const selectedPolicy = policies[Math.floor(Math.random() * policies.length)];
    
    return {
      policy: selectedPolicy,
      marketImpact: selectedPolicy === 'trade_agreement' ? 0.15 : -0.1
    };
  }

  private generateMonetaryPolicyEvent(): any {
    const policies = ['quantitative_easing', 'tightening', 'forward_guidance'];
    const selectedPolicy = policies[Math.floor(Math.random() * policies.length)];
    
    return {
      policy: selectedPolicy,
      marketImpact: selectedPolicy === 'quantitative_easing' ? 0.2 : -0.15
    };
  }

  private generateFiscalStimulusEvent(): any {
    const stimulusSize = 0.5 + Math.random() * 2.0; // 0.5% to 2.5% of GDP
    
    return {
      size: stimulusSize,
      marketImpact: stimulusSize * 0.1
    };
  }

  private generateCurrencyCrisisEvent(): any {
    const devaluation = 0.1 + Math.random() * 0.4; // 10% to 50% devaluation
    
    return {
      devaluation,
      marketImpact: -devaluation * 0.5
    };
  }

  private selectWeightedEvent(events: any[]): any {
    const totalProbability = events.reduce((sum, event) => sum + event.probability, 0);
    const random = Math.random() * totalProbability;
    
    let cumulative = 0;
    for (const event of events) {
      cumulative += event.probability;
      if (random <= cumulative) {
        return event;
      }
    }
    
    return null;
  }

  private createMarketEvent(eventData: any): MarketEvent {
    return {
      id: `economic_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'economic',
      name: `Economic Event: ${eventData.type}`,
      description: `Economic policy change affecting market conditions (Impact: ${(eventData.impact.marketImpact * 100).toFixed(0)}%)`,
      probability: 1.0, // Event is happening
      triggers: [],
      effects: {
        marketSize: eventData.impact.marketImpact,
        segments: [],
        competitors: [],
        duration: 3 + Math.floor(Math.random() * 6) // 3-9 months
      }
    };
  }

  // Additional helper methods
  private initializeEconomicCycle(): EconomicCycle {
    return {
      phase: 'expansion',
      duration: 84, // 7 years
      remainingMonths: 36,
      gdpGrowth: 2.5,
      consumerConfidence: 75,
      unemploymentRate: 5.2,
      inflationRate: 2.1,
      marketMultiplier: 1.1
    };
  }

  private initializeIndicators(): void {
    const indicators = [
      { name: 'gdp_growth', value: 0.025, trend: 'stable' as const, volatility: 0.02, impact: 0.8 },
      { name: 'consumer_confidence', value: 75, trend: 'rising' as const, volatility: 0.05, impact: 0.6 },
      { name: 'unemployment', value: 0.052, trend: 'falling' as const, volatility: 0.01, impact: -0.5 },
      { name: 'inflation', value: 0.021, trend: 'stable' as const, volatility: 0.03, impact: -0.3 },
      { name: 'market_volatility', value: 0.15, trend: 'stable' as const, volatility: 0.1, impact: -0.4 },
      { name: 'business_investment', value: 0.08, trend: 'rising' as const, volatility: 0.04, impact: 0.7 },
      { name: 'trade_balance', value: -0.02, trend: 'stable' as const, volatility: 0.06, impact: 0.3 }
    ];

    indicators.forEach(indicator => {
      this.indicators.set(indicator.name, indicator);
    });
  }

  private initializeCycleTriggers(): void {
    this.cycleTriggers = [
      {
        type: 'time_based',
        condition: 'remaining_months <= 0',
        threshold: 0,
        probability: 1.0
      },
      {
        type: 'indicator_based',
        condition: 'unemployment > 0.08',
        threshold: 0.08,
        probability: 0.7
      },
      {
        type: 'indicator_based',
        condition: 'gdp_growth < -0.02',
        threshold: -0.02,
        probability: 0.8
      },
      {
        type: 'indicator_based',
        condition: 'inflation > 0.05',
        threshold: 0.05,
        probability: 0.6
      }
    ];
  }

  private getPhaseEffect(phase: EconomicCycle['phase'], indicatorName: string): number {
    const phaseEffects: { [key: string]: { [key: string]: number } } = {
      recession: {
        gdp_growth: -0.03,
        consumer_confidence: -0.1,
        unemployment: 0.02,
        inflation: -0.01,
        market_volatility: 0.05
      },
      recovery: {
        gdp_growth: 0.01,
        consumer_confidence: 0.05,
        unemployment: -0.01,
        inflation: 0.005,
        market_volatility: -0.02
      },
      expansion: {
        gdp_growth: 0.02,
        consumer_confidence: 0.03,
        unemployment: -0.005,
        inflation: 0.01,
        market_volatility: -0.01
      },
      peak: {
        gdp_growth: 0.01,
        consumer_confidence: 0.02,
        unemployment: -0.002,
        inflation: 0.02,
        market_volatility: 0.02
      },
      contraction: {
        gdp_growth: -0.01,
        consumer_confidence: -0.05,
        unemployment: 0.01,
        inflation: -0.005,
        market_volatility: 0.03
      }
    };

    return phaseEffects[phase]?.[indicatorName] || 0;
  }

  private calculateExternalEffect(indicatorName: string, marketData: any): number {
    // Simplified external effect calculation
    let effect = 0;
    
    if (marketData.globalEvents) {
      const economicEvents = marketData.globalEvents.filter((event: MarketEvent) => event.type === 'economic');
      effect += economicEvents.length * 0.01;
    }
    
    return effect;
  }

  private getTrendEffect(indicator: EconomicIndicator): number {
    const trendEffects = {
      rising: 0.005,
      falling: -0.005,
      stable: 0
    };
    return trendEffects[indicator.trend];
  }

  private evaluateTrigger(trigger: CycleTrigger): boolean {
    if (trigger.type === 'indicator_based') {
      const indicatorName = trigger.condition.split(' ')[0];
      const operator = trigger.condition.split(' ')[1];
      const threshold = trigger.threshold;
      
      const indicator = this.indicators.get(indicatorName);
      if (!indicator) return false;
      
      switch (operator) {
        case '>': return indicator.value > threshold;
        case '<': return indicator.value < threshold;
        case '>=': return indicator.value >= threshold;
        case '<=': return indicator.value <= threshold;
        default: return false;
      }
    }
    return false;
  }

  private transitionToNextPhase(): void {
    const currentPhase = this.economicCycle.phase;
    const nextPhase = this.getNextPhase(currentPhase);
    
    this.cycleHistory.push({
      timestamp: Date.now(),
      phase: currentPhase,
      duration: this.economicCycle.duration - this.economicCycle.remainingMonths
    });
    
    this.economicCycle.phase = nextPhase;
    this.economicCycle.duration = this.getPhaseTypicalDuration(nextPhase);
    this.economicCycle.remainingMonths = this.economicCycle.duration;
  }

  private getNextPhase(currentPhase: EconomicCycle['phase']): EconomicCycle['phase'] {
    const transitions: { [key: string]: EconomicCycle['phase'] } = {
      recession: 'recovery',
      recovery: 'expansion',
      expansion: 'peak',
      peak: 'contraction',
      contraction: 'recession'
    };
    return transitions[currentPhase] || 'expansion';
  }

  private getPhaseTypicalDuration(phase: EconomicCycle['phase']): number {
    const durations = {
      recession: 18,
      recovery: 24,
      expansion: 36,
      peak: 12,
      contraction: 18
    };
    return durations[phase] || 24;
  }

  private forceTransition(targetPhase: EconomicCycle['phase']): void {
    this.economicCycle.phase = targetPhase;
    this.economicCycle.duration = this.getPhaseTypicalDuration(targetPhase);
    this.economicCycle.remainingMonths = this.economicCycle.duration;
  }

  private calculateCombinedShockSeverity(): number {
    if (this.activeShocks.length === 0) return 0;
    
    return this.activeShocks.reduce((total, shock) => {
      return total + this.getShockSeverityMultiplier(shock.severity);
    }, 0) / this.activeShocks.length;
  }

  private applyShockRecovery(shock: EconomicShock): void {
    // Apply recovery pattern effects
    const recoveryBoost = 0.1; // 10% recovery boost
    
    switch (shock.recoveryPattern) {
      case 'v_shaped':
        this.adjustIndicator('gdp_growth', recoveryBoost);
        break;
      case 'u_shaped':
        this.adjustIndicator('gdp_growth', recoveryBoost * 0.5);
        break;
      case 'l_shaped':
        // Minimal recovery
        this.adjustIndicator('gdp_growth', recoveryBoost * 0.2);
        break;
      case 'w_shaped':
        // Double-dip risk
        if (Math.random() < 0.3) {
          this.adjustIndicator('gdp_growth', -recoveryBoost * 0.5);
        }
        break;
    }
  }

  private applyShockEffects(shock: EconomicShock): void {
    Object.entries(shock.effects).forEach(([key, value]) => {
      this.adjustIndicator(key, value * 0.1); // Apply 10% of effect per month
    });
  }

  private adjustIndicator(name: string, adjustment: number): void {
    const indicator = this.indicators.get(name);
    if (indicator) {
      indicator.value = Math.max(0, indicator.value + adjustment);
    }
  }

  private generateRandomShock(): EconomicShock {
    const shockTypes = ['supply', 'demand', 'financial', 'geopolitical'] as const;
    const severities = ['minor', 'moderate', 'major', 'catastrophic'] as const;
    const recoveryPatterns = ['v_shaped', 'u_shaped', 'l_shaped', 'w_shaped'] as const;
    
    return {
      id: `shock_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: 'Economic Shock',
      type: shockTypes[Math.floor(Math.random() * shockTypes.length)],
      severity: severities[Math.floor(Math.random() * severities.length)],
      duration: 3 + Math.floor(Math.random() * 9), // 3-12 months
      effects: {
        gdpGrowth: -0.02 - Math.random() * 0.03,
        inflation: -0.01 + Math.random() * 0.02,
        unemployment: 0.01 + Math.random() * 0.02,
        consumerConfidence: -5 - Math.random() * 15,
        marketVolatility: 0.05 + Math.random() * 0.15
      },
      affectedRegions: ['global'],
      recoveryPattern: recoveryPatterns[Math.floor(Math.random() * recoveryPatterns.length)]
    };
  }

  private calculateMarketMultiplier(): number {
    const gdp = this.economicCycle.gdpGrowth;
    const confidence = this.economicCycle.consumerConfidence / 100;
    const unemployment = 1 - this.economicCycle.unemploymentRate;
    const inflation = Math.max(0, 1 - Math.abs(this.economicCycle.inflationRate - 0.02) * 10);
    
    return (gdp * 2 + confidence + unemployment + inflation) / 5;
  }

  private identifyTransitionTriggers(currentPhase: EconomicCycle['phase'], nextPhase: EconomicCycle['phase']): string[] {
    // Identify key factors that would trigger transition
    const triggers: string[] = [];
    
    if (nextPhase === 'recession') {
      triggers.push('Rising unemployment', 'Declining GDP', 'Market volatility');
    } else if (nextPhase === 'recovery') {
      triggers.push('Stabilizing employment', 'Fiscal stimulus', 'Consumer confidence recovery');
    } else if (nextPhase === 'expansion') {
      triggers.push('Strong GDP growth', 'Low unemployment', 'Business investment');
    }
    
    return triggers;
  }

  private calculateTransitionProbabilities(months: number): Array<{ month: number; probability: number }> {
    const probabilities = [];
    const currentRemaining = this.economicCycle.remainingMonths;
    
    for (let month = 1; month <= months; month++) {
      let probability = 0;
      
      if (month >= currentRemaining) {
        // Time-based transition probability
        probability = 0.8;
      } else {
        // Early transition probability based on indicators
        probability = Math.max(0, (months - currentRemaining + month) / months * 0.3);
      }
      
      probabilities.push({ month, probability });
    }
    
    return probabilities;
  }

  private estimatePhaseRemaining(): number {
    return this.economicCycle.remainingMonths;
  }

  // Public getters
  public getEconomicCycle(): EconomicCycle {
    return this.economicCycle;
  }

  public getIndicators(): Map<string, EconomicIndicator> {
    return this.indicators;
  }

  public getActiveShocks(): EconomicShock[] {
    return this.activeShocks;
  }

  public getCycleHistory(): Array<{ timestamp: number; phase: EconomicCycle['phase']; duration: number }> {
    return this.cycleHistory;
  }

  /**
   * Reset the service to initial state
   */
  public reset(): void {
    // console.log('💹 Economic Cycle Service: Resetting to initial state');
    
    // Reset economic cycle to initial state
    this.economicCycle = this.initializeEconomicCycle();
    
    // Clear and re-initialize indicators
    this.indicators.clear();
    this.initializeIndicators();
    
    // Clear and re-initialize cycle triggers
    this.cycleTriggers = [];
    this.initializeCycleTriggers();
    
    // Clear all active shocks
    this.activeShocks = [];
    
    // Clear cycle history
    this.cycleHistory = [];
    
    // console.log('💹 Economic Cycle Service: Reset complete');
  }
}

export const economicCycle = EconomicCycleService.getInstance();